"use client";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@radix-ui/react-tooltip";
import { Editor } from "@tiptap/react";
import {
	BoldIcon,
	CaseSensitiveIcon,
	CornerDownLeftIcon,
	ItalicIcon,
	LinkIcon,
	ListIcon,
	ListOrderedIcon,
	LucideIcon,
	MinusIcon,
	PaperclipIcon,
	Redo2Icon,
	StrikethroughIcon,
	Undo2Icon,
	YoutubeIcon,
	icons,
} from "lucide-react";
import { useCallback, useState } from "react";
import EditorImageUploadModal from "./image-upload-modal";

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const EditorOptionButton = ({
	icon: Icon,
	active,
	hidden,
	disabled,
	title,
	onClick,
}: {
	icon: LucideIcon;
	active?: boolean;
	hidden?: boolean;
	disabled?: boolean;
	title?: string;
	onClick: () => void;
}) => {
	return !hidden ? (
		<TooltipProvider>
			<Tooltip delayDuration={0}>
				<TooltipTrigger
					type="button"
					onClick={onClick}
					className={cn(
						"rounded-lg p-2 text-sm text-gray-800 disabled:cursor-not-allowed disabled:opacity-60",
						active ? "bg-gray-300" : ""
					)}
					disabled={disabled}
				>
					<Icon className="h-4 w-4" />
				</TooltipTrigger>
				<TooltipContent>
					<p className="rounded border bg-white px-2 py-1 text-xxs font-medium text-gray-700">
						{title}
					</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	) : null;
};

const EditorOptionsMenu = ({
	editor,
	disableOptions = [],
}: {
	editor: Editor | null;
	disableOptions?: string[];
}) => {
	const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);
	const isEditorDisabled = !editor?.isEditable;

	const setLink = useCallback(() => {
		if (!editor) return;

		const previousUrl = editor.getAttributes("link").href;
		const url = window.prompt("URL", previousUrl);

		if (url === null) return;

		if (url === "") {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
			return;
		}

		editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
	}, [editor]);

	const addYouTubeVideo = useCallback(() => {
		const url = prompt("Enter YouTube URL");

		if (editor && url) {
			editor.commands.setYoutubeVideo({ src: url });
		}
	}, [editor]);

	if (!editor) return null;

	return (
		<>
			<div className="flex w-fit flex-wrap items-center justify-start gap-2 rounded-md border bg-gray-100 p-1">
				<EditorOptionButton
					title="Link"
					icon={LinkIcon}
					onClick={setLink}
					active={editor.isActive("link")}
					hidden={disableOptions.includes("Link")}
					disabled={isEditorDisabled}
				/>
				<EditorOptionButton
					title="Bold"
					icon={BoldIcon}
					active={editor.isActive("bold")}
					onClick={() => editor.chain().focus().toggleBold().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleBold().run()}
					hidden={disableOptions.includes("Bold")}
				/>
				<EditorOptionButton
					title="Italic"
					icon={ItalicIcon}
					active={editor.isActive("italic")}
					onClick={() => editor.chain().focus().toggleItalic().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleItalic().run()}
					hidden={disableOptions.includes("Italic")}
				/>
				<EditorOptionButton
					title="Strike"
					icon={StrikethroughIcon}
					active={editor.isActive("strike")}
					onClick={() => editor.chain().focus().toggleStrike().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleStrike().run()}
					hidden={disableOptions.includes("Strike")}
				/>
				<EditorOptionButton
					title="Paragraph"
					icon={CaseSensitiveIcon}
					active={editor.isActive("paragraph")}
					onClick={() => editor.chain().focus().setParagraph().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().setParagraph().run()}
					hidden={disableOptions.includes("Paragraph")}
				/>
				{([1, 2, 3] as HeadingLevel[]).map((level) => (
					<EditorOptionButton
						key={level}
						title={`Heading ${level}`}
						icon={icons[`Heading${level}` as keyof typeof icons]}
						active={editor.isActive("heading", { level })}
						onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
						disabled={
							isEditorDisabled || !editor.can().chain().focus().toggleHeading({ level }).run()
						}
						hidden={disableOptions.includes(`Heading ${level}`)}
					/>
				))}
				<EditorOptionButton
					title="Image"
					icon={PaperclipIcon}
					onClick={() => setIsImageUploadModalOpen(true)}
					hidden={disableOptions.includes("Image")}
					disabled={isEditorDisabled}
				/>
				<EditorOptionButton
					title="YouTube"
					icon={YoutubeIcon}
					active={editor.isActive("youtube")}
					onClick={addYouTubeVideo}
					hidden={disableOptions.includes("YouTube")}
					disabled={isEditorDisabled}
				/>
				<EditorOptionButton
					icon={ListIcon}
					title="Bullet List"
					active={editor.isActive("bulletList")}
					onClick={() => editor.chain().focus().toggleBulletList().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleBulletList().run()}
					hidden={disableOptions.includes("Bullet List")}
				/>
				<EditorOptionButton
					title="Ordered List"
					icon={ListOrderedIcon}
					active={editor.isActive("orderedList")}
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleOrderedList().run()}
					hidden={disableOptions.includes("Ordered List")}
				/>
				<EditorOptionButton
					title="Divider"
					icon={MinusIcon}
					onClick={() => editor.chain().focus().setHorizontalRule().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().setHorizontalRule().run()}
					hidden={disableOptions.includes("Divider")}
				/>
				<EditorOptionButton
					title="Line Break"
					icon={CornerDownLeftIcon}
					onClick={() => editor.chain().focus().setHardBreak().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().setHardBreak().run()}
					hidden={disableOptions.includes("Line Break")}
				/>
				<EditorOptionButton
					title="Undo"
					icon={Undo2Icon}
					onClick={() => editor.chain().focus().undo().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().undo().run()}
					hidden={disableOptions.includes("Undo")}
				/>
				<EditorOptionButton
					title="Redo"
					icon={Redo2Icon}
					onClick={() => editor.chain().focus().redo().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().redo().run()}
					hidden={disableOptions.includes("Redo")}
				/>
			</div>

			<EditorImageUploadModal
				editor={editor}
				isOpen={isImageUploadModalOpen}
				onClose={() => setIsImageUploadModalOpen(false)}
			/>
		</>
	);
};

export default EditorOptionsMenu;
